import * as React from 'react';
import cn from 'classnames';

import {
	GetServicesRefundInfoQuery,
	OrderAdditionalServiceGdsServiceServiceType,
	ExareGdsServiceRefundInfo,
	PassengerFragment,
	InsuranceProgramFragment,
	FlightFragment,
	CheckinSegmentFragment,
	CheckinPassengerFragment
} from '@websky/graphql';
import { groupRefundServicesByPassenger, groupServicesByType } from '../utils';
import Baggage from './Services/Baggage/Baggage';
import { RefundServices } from '../types';
import Meal from './Services/Meal/Meal';
import { useTheme } from '../../theme';
import Seat from './Services/Seat/Seat';
import Insurance from './Services/Insurance/Insurance';
import { useCallback } from 'react';
import { useRenderers } from '../../renderProps';
import { ServiceCardBaseProps } from './Services/ServiceCard/ServiceCard';
import { CardExtraViewSkeleton, CardExtraViewVariant } from '@shared/ui/CardExtraView';

interface Props {
	className?: string;
	noteClassName?: string;
	flight: FlightFragment | CheckinSegmentFragment[];
	travellers: (PassengerFragment | CheckinPassengerFragment)[];
	gdsServicesRefund?: GetServicesRefundInfoQuery;
	insurancesRefund?: InsuranceProgramFragment[];
	notification?: React.ReactNode;
	isLoading?: boolean;
	customWarningMessage?: React.ReactElement;
	mode?: CardExtraViewVariant;
}

const RefundSummary: React.FC<Props> = ({
	className,
	flight,
	travellers,
	gdsServicesRefund,
	insurancesRefund,
	notification,
	isLoading,
	customWarningMessage,
	mode
}) => {
	const theme = useTheme('RefundSummary').RefundSummary;
	const { RefundSummaryInsurance: RefundSummaryInsuranceOverride = Insurance } = useRenderers();

	const groupServices = React.useMemo(() => {
		if (gdsServicesRefund?.GetGdsServicesRefundInfo?.gdsServicesRefundInfo) {
			const servicesByType = groupServicesByType(
				gdsServicesRefund?.GetGdsServicesRefundInfo?.gdsServicesRefundInfo as ExareGdsServiceRefundInfo[]
			);

			const refundServicesByType = new Map<OrderAdditionalServiceGdsServiceServiceType, RefundServices>();

			servicesByType.forEach((value, key) => {
				refundServicesByType.set(key, groupRefundServicesByPassenger(value));
			});

			return refundServicesByType;
		}

		return null;
	}, [gdsServicesRefund]);

	const getServiceCardByType = (
		refundServices: RefundServices,
		type: OrderAdditionalServiceGdsServiceServiceType
	) => {
		const props: ServiceCardBaseProps = {
			flight,
			passengers: travellers as PassengerFragment[],
			services: refundServices,
			mode
		};

		switch (type) {
			case OrderAdditionalServiceGdsServiceServiceType.Baggage:
				return <Baggage {...props} />;

			case OrderAdditionalServiceGdsServiceServiceType.Meal:
				return <Meal {...props} />;

			case OrderAdditionalServiceGdsServiceServiceType.Seat:
				return <Seat {...props} />;
		}

		return null;
	};

	const renderServicesCard = React.useMemo(() => {
		const services: React.ReactNode[] = [];

		groupServices?.forEach((value, key) => {
			if (Object.keys(value).length > 0) {
				services.push(getServiceCardByType(value, key));
			}
		});

		if (insurancesRefund?.length && (flight as FlightFragment)?.segmentGroups !== undefined) {
			services.push(<RefundSummaryInsuranceOverride services={insurancesRefund} />);
		}

		if (services.length) {
			return services.map((component, key) => {
				return <React.Fragment key={key}>{component}</React.Fragment>;
			});
		}

		return null;
	}, [groupServices, insurancesRefund, flight, mode]);

	const WarningMessage = useCallback(() => {
		if (customWarningMessage) {
			return customWarningMessage;
		}

		return (
			<div className={theme.warningMessage}>
				{notification && <div className={theme.notifications}>{notification}</div>}

				<div className={cn(theme.services, className)}>{renderServicesCard}</div>
			</div>
		);
	}, [isLoading, notification, renderServicesCard, customWarningMessage]);

	return (
		<>
			{isLoading && (
				<div className={cn(theme.container, className)}>
					<CardExtraViewSkeleton />
					<CardExtraViewSkeleton />
					<CardExtraViewSkeleton />
				</div>
			)}

			{!isLoading && !!renderServicesCard && <WarningMessage />}
		</>
	);
};

export default RefundSummary;
